import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  TextField,
  CircularProgress,
  FormHelperText
} from '@mui/material';
import { FileDownload } from '@mui/icons-material';
import { useAppData } from '../../context/AppDataContext';
import { fetchReportDefinitions, exportReportFile, exportCollectionReport } from '../../services/reportService';

const ReportsDashboard = () => {
  // State for filters
  const [selectedBranch, setSelectedBranch] = useState('');
  const [startDate, setStartDate] = useState(() => moment().format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(() => moment().format('YYYY-MM-DD'));
  const [reportType, setReportType] = useState('sale-report');
  const [reportOptions, setReportOptions] = useState([]);
  const [reportOptionsLoading, setReportOptionsLoading] = useState(false);
  const [reportOptionsError, setReportOptionsError] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState('');

  // State for collection report section
  const [collectionStartDate, setCollectionStartDate] = useState(() => {
    // Set default to today's date
    return moment().format('YYYY-MM-DD');
  });
  const [collectionStartTime, setCollectionStartTime] = useState('00:01');
  const [collectionEndDate, setCollectionEndDate] = useState(() => {
    // Set default to today's date
    return moment().format('YYYY-MM-DD');
  });
  const [collectionEndTime, setCollectionEndTime] = useState('23:59:59');
  const [selectedCollector, setSelectedCollector] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState('');

  // Hardcoded list of collectors (Người thực hiện thu tiền)
  const collectors = [
    { id: '1', name: 'LÊ THỊ  TUYẾT HƯƠNG' },
    { id: '2', name: 'ĐỖ PHẠM THU PHƯƠNG' },
    { id: '3', name: 'TRẦN HỒNG QUÂN' }
  ];

  const { getStoreList } = useAppData();
  const stores = getStoreList();

  useEffect(() => {
    if (selectedBranch) return;
    if (!Array.isArray(stores) || stores.length === 0) return;

    const defaultBranch = stores.find((store) => store?.name === '33 Đồng Khởi');
    if (defaultBranch?.id) {
      setSelectedBranch(defaultBranch.id);
    }
  }, [stores, selectedBranch]);

  useEffect(() => {
    const loadReportOptions = async () => {
      setReportOptionsLoading(true);
      setReportOptionsError('');
      try {
        const response = await fetchReportDefinitions();
        setReportOptions(response || []);
      } catch (error) {
        setReportOptionsError('Unable to load report types. Please try again later.');
      } finally {
        setReportOptionsLoading(false);
      }
    };

    loadReportOptions();
  }, []);

  const isFormValid = useMemo(() => {
    return Boolean(selectedBranch && startDate && endDate && reportType);
  }, [selectedBranch, startDate, endDate, reportType]);

  const isCollectionFormValid = useMemo(() => {
    return Boolean(collectionStartDate && collectionStartTime && collectionEndDate && collectionEndTime && selectedCollector);
  }, [collectionStartDate, collectionStartTime, collectionEndDate, collectionEndTime, selectedCollector]);

  const handleExportReport = async () => {
    if (!isFormValid) {
      setExportError('Please select branch, start date, end date, and report type.');
      return;
    }

    let startTimestamp = moment(startDate).startOf('day').unix() * 1000;
    let endTimestamp = moment(endDate).endOf('day').unix() * 1000;

    if (Number.isNaN(startTimestamp) || Number.isNaN(endTimestamp)) {
      setExportError('Invalid date selection. Please choose valid dates.');
      return;
    }

    if (startTimestamp > endTimestamp) {
      setExportError('Start date must be before end date.');
      return;
    }

    setExportError('');
    setIsExporting(true);

    try {
      const blob = await exportReportFile({
        reportId: reportType,
        startTimestamp,
        endTimestamp,
        clientId: selectedBranch
      });

      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const formattedStart = new Date(startTimestamp).toISOString().split('T')[0];
      const formattedEnd = new Date(endTimestamp).toISOString().split('T')[0];
      link.href = downloadUrl;
      link.download = `${reportType}_${formattedStart}_${formattedEnd}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      setExportError(error.message || 'Failed to export report. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleDownloadCollectionFile = async () => {
    if (!isCollectionFormValid) {
      setDownloadError('Please select start date, start time, end date, end time, and collector.');
      return;
    }

    // Normalize time format to HH:mm:ss
    const normalizeTime = (timeStr) => {
      if (!timeStr || !timeStr.includes(':')) {
        return null;
      }
      
      const parts = timeStr.split(':');
      if (parts.length === 1) {
        // Only hour, add :00:00
        const hour = parts[0].padStart(2, '0');
        if (parseInt(hour) >= 24) return null;
        return `${hour}:00:00`;
      } else if (parts.length === 2) {
        // HH:mm, add :00
        const hour = parts[0].padStart(2, '0');
        const minute = parts[1].padStart(2, '0');
        if (parseInt(hour) >= 24 || parseInt(minute) >= 60) return null;
        return `${hour}:${minute}:00`;
      } else if (parts.length === 3) {
        // HH:mm:ss, normalize
        const hour = parts[0].padStart(2, '0');
        const minute = parts[1].padStart(2, '0');
        const second = parts[2].padStart(2, '0');
        if (parseInt(hour) >= 24 || parseInt(minute) >= 60 || parseInt(second) >= 60) return null;
        return `${hour}:${minute}:${second}`;
      }
      return null;
    };

    const normalizedStartTime = normalizeTime(collectionStartTime);
    const normalizedEndTime = normalizeTime(collectionEndTime);

    if (!normalizedStartTime || !normalizedEndTime) {
      setDownloadError('Invalid time format. Please use HH:mm or HH:mm:ss format.');
      return;
    }

    const finalStartDateTime = moment(`${collectionStartDate} ${normalizedStartTime}`, 'YYYY-MM-DD HH:mm:ss');
    const finalEndDateTime = moment(`${collectionEndDate} ${normalizedEndTime}`, 'YYYY-MM-DD HH:mm:ss');

    if (!finalStartDateTime.isValid() || !finalEndDateTime.isValid()) {
      setDownloadError('Invalid date or time selection. Please check your inputs.');
      return;
    }

    const startTimestamp = finalStartDateTime.unix() * 1000;
    const endTimestamp = finalEndDateTime.unix() * 1000;

    if (startTimestamp > endTimestamp) {
      setDownloadError('Start date and time must be before end date and time.');
      return;
    }

    setDownloadError('');
    setIsDownloading(true);

    try {
      // Find collector name from selected collector ID
      const collectorName = collectors.find(c => c.id === selectedCollector)?.name;
      if (!collectorName) {
        setDownloadError('Please select a valid collector.');
        setIsDownloading(false);
        return;
      }

      const blob = await exportCollectionReport({
        startTimestamp,
        endTimestamp,
        receiverName: collectorName
      });

      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const formattedStart = finalStartDateTime.format('YYYY-MM-DD');
      const formattedEnd = finalEndDateTime.format('YYYY-MM-DD');
      link.href = downloadUrl;
      link.download = `collection_report_${formattedStart}_${formattedEnd}.docx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      setDownloadError(error.message || 'Failed to download file. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box className="mb-6">
        <Typography variant="h4" className="font-bold text-gray-800 mb-2">
          Reports & Analytics
        </Typography>
        <Typography variant="body1" className="text-gray-600">
          Comprehensive reports and insights on warehouse operations
        </Typography>
      </Box>

      {/* XLSX Report Export */}
      <Card className="mb-6 shadow-md">
        <CardContent>
          <Box className="flex items-center gap-2 mb-4">
            <FileDownload className="text-blue-600" />
            <Typography variant="h6" className="font-semibold text-gray-800">
              Export XLSX Report
            </Typography>
          </Box>
          <Divider className="mb-4" />

          <Grid container spacing={3}>
            {/* Branch Selection */}
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth required>
                <InputLabel id="branch-select-label">Branch</InputLabel>
                <Select
                  labelId="branch-select-label"
                  value={selectedBranch}
                  label="Branch"
                  onChange={(e) => setSelectedBranch(e.target.value)}
                >
                  <MenuItem value="" disabled>
                    <em>Select Branch</em>
                  </MenuItem>
                  {stores.map((store) => (
                    <MenuItem key={store.id} value={store.id}>
                      {store.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Start Date */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value)
                }}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>

            {/* End Date */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value)
                }}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>

            {/* Report Type */}
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth required error={Boolean(reportOptionsError)}>
                <InputLabel id="report-type-label">Report Type</InputLabel>
                <Select
                  labelId="report-type-label"
                  value={reportType}
                  label="Report Type"
                  onChange={(e) => {
                    console.log('e.target.value', e.target.value);
                    setReportType(e.target.value)
                  }}
                  disabled={reportOptionsLoading || Boolean(reportOptionsError)}
                >
                  {reportOptionsLoading && (
                    <MenuItem value="" disabled>
                      Loading report types...
                    </MenuItem>
                  )}
                  {!reportOptionsLoading &&
                    !reportOptionsError &&
                    reportOptions.length > 0 &&
                    reportOptions.map((type) => (
                      <MenuItem key={type.id} value={type.id}>
                        {type.name}
                      </MenuItem>
                    ))}
                  {!reportOptionsLoading &&
                    !reportOptionsError &&
                    reportOptions.length === 0 && (
                      <MenuItem value="" disabled>
                        No report types available
                      </MenuItem>
                    )}
                </Select>
                {reportOptionsError && (
                  <FormHelperText>{reportOptionsError}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            {/* Export Button */}
            <Grid item xs={12}>
              <Box className="flex justify-end">
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  startIcon={<FileDownload />}
                  onClick={handleExportReport}
                  className="px-8"
                  disabled={!isFormValid || isExporting}
                >
                  {isExporting ? (
                    <Box className="flex items-center gap-2">
                      <CircularProgress size={20} color="inherit" />
                      Exporting...
                    </Box>
                  ) : (
                    'Export XLSX'
                  )}
                </Button>
              </Box>
              {exportError && (
                <Box className="mt-3">
                  <Typography variant="body2" color="error">
                    {exportError}
                  </Typography>
                </Box>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Collection Report Export */}
      <Card className="mb-6 shadow-md">
        <CardContent>
          <Box className="flex items-center gap-2 mb-4">
            <FileDownload className="text-blue-600" />
            <Typography variant="h6" className="font-semibold text-gray-800">
              Collection Report Export
            </Typography>
          </Box>
          <Divider className="mb-4" />

          <Grid container spacing={3}>
            {/* Start Date */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={collectionStartDate}
                onChange={(e) => {
                  setCollectionStartDate(e.target.value);
                }}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>

            {/* Start Time */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Start Time"
                type="text"
                value={collectionStartTime}
                onChange={(e) => {
                  const value = e.target.value;
                  // Allow input like H:mm, HH:mm, H:mm:ss, or HH:mm:ss
                  // More flexible regex to allow typing
                  if (/^([0-2]?[0-9]?)?(:([0-5]?[0-9]?)?(:([0-5]?[0-9]?)?)?)?$/.test(value) || value === '') {
                    setCollectionStartTime(value);
                  }
                }}
                onBlur={(e) => {
                  // Validate and normalize on blur
                  const value = e.target.value.trim();
                  if (value) {
                    const parts = value.split(':');
                    if (parts.length === 1 && parts[0]) {
                      // If only hour entered, add :00:00
                      const hour = parts[0].padStart(2, '0');
                      if (parseInt(hour) < 24) {
                        setCollectionStartTime(`${hour}:00:00`);
                      }
                    } else if (parts.length === 2) {
                      // If HH:mm, add :00
                      const hour = parts[0].padStart(2, '0');
                      const minute = parts[1].padStart(2, '0');
                      if (parseInt(hour) < 24 && parseInt(minute) < 60) {
                        setCollectionStartTime(`${hour}:${minute}:00`);
                      }
                    } else if (parts.length === 3) {
                      // If HH:mm:ss, normalize
                      const hour = parts[0].padStart(2, '0');
                      const minute = parts[1].padStart(2, '0');
                      const second = parts[2].padStart(2, '0');
                      if (parseInt(hour) < 24 && parseInt(minute) < 60 && parseInt(second) < 60) {
                        setCollectionStartTime(`${hour}:${minute}:${second}`);
                      }
                    }
                  }
                }}
                placeholder="HH:mm:ss"
                helperText="Format: HH:mm:ss (default: 00:01)"
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>

            {/* End Date */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={collectionEndDate}
                onChange={(e) => {
                  setCollectionEndDate(e.target.value);
                }}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>

            {/* End Time */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="End Time"
                type="text"
                value={collectionEndTime}
                onChange={(e) => {
                  const value = e.target.value;
                  // Allow input like H:mm, HH:mm, H:mm:ss, or HH:mm:ss
                  // More flexible regex to allow typing
                  if (/^([0-2]?[0-9]?)?(:([0-5]?[0-9]?)?(:([0-5]?[0-9]?)?)?)?$/.test(value) || value === '') {
                    setCollectionEndTime(value);
                  }
                }}
                onBlur={(e) => {
                  // Validate and normalize on blur
                  const value = e.target.value.trim();
                  if (value) {
                    const parts = value.split(':');
                    if (parts.length === 1 && parts[0]) {
                      // If only hour entered, add :00:00
                      const hour = parts[0].padStart(2, '0');
                      if (parseInt(hour) < 24) {
                        setCollectionEndTime(`${hour}:00:00`);
                      }
                    } else if (parts.length === 2) {
                      // If HH:mm, add :00
                      const hour = parts[0].padStart(2, '0');
                      const minute = parts[1].padStart(2, '0');
                      if (parseInt(hour) < 24 && parseInt(minute) < 60) {
                        setCollectionEndTime(`${hour}:${minute}:00`);
                      }
                    } else if (parts.length === 3) {
                      // If HH:mm:ss, normalize
                      const hour = parts[0].padStart(2, '0');
                      const minute = parts[1].padStart(2, '0');
                      const second = parts[2].padStart(2, '0');
                      if (parseInt(hour) < 24 && parseInt(minute) < 60 && parseInt(second) < 60) {
                        setCollectionEndTime(`${hour}:${minute}:${second}`);
                      }
                    }
                  }
                }}
                placeholder="HH:mm:ss"
                helperText="Format: HH:mm:ss (default: 23:59:59)"
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>

            {/* Collector Selection */}
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth required>
                <InputLabel id="collector-select-label">Người thực hiện thu tiền</InputLabel>
                <Select
                  labelId="collector-select-label"
                  value={selectedCollector}
                  label="Người thực hiện thu tiền"
                  onChange={(e) => setSelectedCollector(e.target.value)}
                >
                  <MenuItem value="" disabled>
                    <em>Select Collector</em>
                  </MenuItem>
                  {collectors.map((collector) => (
                    <MenuItem key={collector.id} value={collector.id}>
                      {collector.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Download Button */}
            <Grid item xs={12}>
              <Box className="flex justify-end">
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  startIcon={<FileDownload />}
                  onClick={handleDownloadCollectionFile}
                  className="px-8"
                  disabled={!isCollectionFormValid || isDownloading}
                >
                  {isDownloading ? (
                    <Box className="flex items-center gap-2">
                      <CircularProgress size={20} color="inherit" />
                      Downloading...
                    </Box>
                  ) : (
                    'Download File'
                  )}
                </Button>
              </Box>
              {downloadError && (
                <Box className="mt-3">
                  <Typography variant="body2" color="error">
                    {downloadError}
                  </Typography>
                </Box>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ReportsDashboard;
